import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Input, Row } from 'antd';
import React from 'react';
import { NUMBER_FIELD_TYPES } from '../../common/constants';
import NumberComponent from '../NumberComponent';
import { getChecklistKey, getContentKey, hasPieceCount } from './utils';
import './unitScan.less';

const { TextArea } = Input;

const ANSWER_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const UnitInspectionForm = ({
  inspectionForm,
  pieceCounts,
  onPieceCountChange,
  checklistAnswers,
  onChecklistAnswerChange,
  errors,
  notes,
  onNotesChange,
  disabled,
  showNotes = true,
}) => {
  const contents = inspectionForm?.requiredContents || [];
  const checklist = inspectionForm?.checklist || [];

  const getCount = (key) => {
    if (!hasPieceCount(pieceCounts?.[key])) return undefined;
    const count = Number(pieceCounts?.[key]);
    return Number.isFinite(count) ? count : undefined;
  };

  return (
    <div className="unit-scan-form">
      {contents?.length > 0 && (
        <div className="unit-scan-section unit-scan-card">
          <div className="unit-scan-section-title">
            Contents
            <span className="unit-scan-section-subtitle">
              Enter the number of pieces present
            </span>
          </div>
          {contents?.map((content) => {
            const key = getContentKey(content);
            const count = getCount(key);
            const isEmpty = count === undefined;
            const error = errors?.[key];
            const maximum = Number(content?.maximumQuantity);
            const hasMaximum = Number.isFinite(maximum);
            const isAtMaximum = hasMaximum && count >= maximum;
            const capCount = (value) =>
              hasMaximum ? Math.min(value, maximum) : value;
            return (
              <Row key={key} gutter={[8, 0]} className="unit-scan-row">
                <Col xs={14}>
                  <div>{content?.name}</div>
                  {content?.description && (
                    <div className="font-size-12">{content?.description}</div>
                  )}
                </Col>
                <Col xs={10}>
                  <div className="unit-scan-count">
                    <div className="unit-scan-stepper">
                      <Button
                        shape="circle"
                        icon={<MinusOutlined />}
                        disabled={disabled || count <= 0}
                        onClick={() =>
                          onPieceCountChange(key, isEmpty ? 0 : count - 1)
                        }
                      />
                      <NumberComponent
                        allowClear={false}
                        isFractional={false}
                        disabled={disabled}
                        className="unit-scan-stepper-input"
                        placeholder="Qty"
                        type={NUMBER_FIELD_TYPES?.QUANTITY}
                        status={error ? 'error' : undefined}
                        value={isEmpty ? '' : count}
                        onChange={(event) => {
                          const parsed = parseInt(event?.target?.value, 10);
                          onPieceCountChange(
                            key,
                            Number.isNaN(parsed) ? undefined : capCount(parsed),
                          );
                        }}
                      />
                      <Button
                        shape="circle"
                        icon={<PlusOutlined />}
                        disabled={disabled || isAtMaximum}
                        onClick={() =>
                          onPieceCountChange(
                            key,
                            capCount(isEmpty ? 1 : count + 1),
                          )
                        }
                      />
                    </div>
                    {error && <div className="unit-scan-error">{error}</div>}
                    <div className="unit-scan-hint">
                      Required {content?.minimumQuantity ?? 0} of{' '}
                      {content?.maximumQuantity ?? 0}
                    </div>
                  </div>
                </Col>
              </Row>
            );
          })}
        </div>
      )}

      {checklist?.length > 0 && (
        <div className="unit-scan-section unit-scan-card">
          <div className="unit-scan-section-title">
            Checklist
            <span className="unit-scan-section-subtitle">
              Answer each question
            </span>
          </div>
          {checklist?.map((item) => {
            const key = getChecklistKey(item);
            const error = errors?.[key];
            const answer = checklistAnswers?.[key];
            return (
              <Row key={key} gutter={[8, 0]} className="unit-scan-row">
                <Col xs={14}>
                  <div>{item?.text}</div>
                </Col>
                <Col xs={10}>
                  <div className="unit-scan-field">
                    <div className="unit-scan-answer">
                      {ANSWER_OPTIONS?.map((option) => (
                        <Button
                          key={option?.label}
                          disabled={disabled}
                          danger={error ? true : undefined}
                          type={answer === option?.value ? 'primary' : 'default'}
                          onClick={() =>
                            onChecklistAnswerChange(
                              key,
                              answer === option?.value
                                ? undefined
                                : option?.value,
                            )
                          }
                        >
                          {option?.label}
                        </Button>
                      ))}
                    </div>
                    {error && <div className="unit-scan-error">{error}</div>}
                  </div>
                </Col>
              </Row>
            );
          })}
        </div>
      )}

      {contents?.length === 0 && checklist?.length === 0 && (
        <Alert
          className="unit-scan-empty"
          type="info"
          showIcon
          message="This toy has no contents or checklist to verify."
        />
      )}

      {showNotes && (
        <div className="unit-scan-section">
          <div className="unit-scan-section-title">Notes (optional)</div>
          <TextArea
            rows={2}
            allowClear
            maxLength={500}
            disabled={disabled}
            placeholder="Anything worth recording"
            value={notes}
            onChange={(event) => onNotesChange(event?.target?.value)}
          />
        </div>
      )}
    </div>
  );
};

export default UnitInspectionForm;
